import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { motion } from 'motion/react';
import { Dialog } from 'radix-ui';

import { useDialogContext } from '@components/primitives/Dialog/context';
import {
  composeModalHandlers,
  useModalPartsContext,
} from '@components/primitives/focus';
import { cn, easeOutExpo } from '@components/primitives/utilities';

import { DrawerScrollContext, useDrawerDirection } from './context';

import css from './Drawer.module.css';

type DialogContentEventProps = Pick<
  ComponentPropsWithoutRef<typeof Dialog.Content>,
  | 'onOpenAutoFocus'
  | 'onCloseAutoFocus'
  | 'onEscapeKeyDown'
  | 'onPointerDownOutside'
  | 'onInteractOutside'
  | 'onFocusOutside'
>;

type DrawerContentProps = Omit<
  ComponentPropsWithoutRef<typeof motion.div>,
  'children' | keyof DialogContentEventProps
> &
  DialogContentEventProps & {
    children?: ReactNode;
  };

type ScrollPosition = 'top' | 'middle' | 'bottom';

const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  function DrawerContent(
    {
      className,
      children,
      onScroll,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onInteractOutside,
      onFocusOutside,
      ...props
    },
    forwardedRef
  ) {
    const direction = useDrawerDirection();
    const state = useDialogContext();
    const parts = useModalPartsContext();
    const ref = useRef<HTMLDivElement>(null);

    const [scrollPosition, setScrollPosition] = useState<ScrollPosition>('top');
    const handleScroll = useCallback(() => {
      const area = ref.current;
      if (!area) return;

      const scrollBottom = Math.ceil(area.scrollTop + area.clientHeight);
      if (area.scrollTop === 0) {
        setScrollPosition('top');
      } else if (scrollBottom >= area.scrollHeight) {
        setScrollPosition('bottom');
      } else {
        setScrollPosition('middle');
      }
    }, []);

    const [scrollable, setScrollable] = useState(false);
    useEffect(() => {
      const area = ref.current;
      if (!area) return;

      const observer = new ResizeObserver(() => {
        setScrollable(area.scrollHeight > area.clientHeight);
        handleScroll();
      });
      observer.observe(area);

      return () => observer.disconnect();
    }, [state.isOpen, handleScroll]);

    return (
      <Dialog.Content
        onOpenAutoFocus={composeModalHandlers(
          parts.onOpenAutoFocus,
          onOpenAutoFocus
        )}
        onCloseAutoFocus={composeModalHandlers(
          parts.onCloseAutoFocus,
          onCloseAutoFocus
        )}
        onEscapeKeyDown={composeModalHandlers(
          parts.onEscapeKeyDown,
          onEscapeKeyDown
        )}
        onPointerDownOutside={onPointerDownOutside}
        onInteractOutside={composeModalHandlers(
          parts.onInteractOutside,
          onInteractOutside
        )}
        onFocusOutside={onFocusOutside}
        asChild
      >
        <motion.div
          ref={composeRefs(ref, forwardedRef)}
          className={cn(css.drawer, className)}
          data-direction={direction}
          data-scroll-position={scrollPosition}
          data-scrollable={scrollable}
          {...DRAWER_ANIMATIONS}
          {...props}
          onScroll={(event) => {
            handleScroll();
            onScroll?.(event);
          }}
        >
          <DrawerScrollContext.Provider value={ref}>
            {children}
          </DrawerScrollContext.Provider>
        </motion.div>
      </Dialog.Content>
    );
  }
);

const DRAWER_ANIMATIONS = {
  initial: { '--progress': 0, opacity: 0 },
  animate: { '--progress': 1, opacity: 1 },
  exit: { '--progress': 0, opacity: 0 },
  transition: { duration: 0.3, ease: easeOutExpo },
};

export default DrawerContent;
